// Hebrew Gematria Cipher Definitions
// Based on traditional Hebrew numerology systems

// Hebrew alphabet with standard values
const hebrewAlphabet = {
  // Regular letters
  'א': { standard: 1, ordinal: 1, name: 'Alef' },
  'ב': { standard: 2, ordinal: 2, name: 'Bet' },
  'ג': { standard: 3, ordinal: 3, name: 'Gimel' },
  'ד': { standard: 4, ordinal: 4, name: 'Dalet' },
  'ה': { standard: 5, ordinal: 5, name: 'He' },
  'ו': { standard: 6, ordinal: 6, name: 'Vav' },
  'ז': { standard: 7, ordinal: 7, name: 'Zayin' },
  'ח': { standard: 8, ordinal: 8, name: 'Chet' },
  'ט': { standard: 9, ordinal: 9, name: 'Tet' },
  'י': { standard: 10, ordinal: 10, name: 'Yod' },
  'כ': { standard: 20, ordinal: 11, name: 'Kaf' },
  'ל': { standard: 30, ordinal: 12, name: 'Lamed' },
  'מ': { standard: 40, ordinal: 13, name: 'Mem' },
  'נ': { standard: 50, ordinal: 14, name: 'Nun' },
  'ס': { standard: 60, ordinal: 15, name: 'Samech' },
  'ע': { standard: 70, ordinal: 16, name: 'Ayin' },
  'פ': { standard: 80, ordinal: 17, name: 'Pe' },
  'צ': { standard: 90, ordinal: 18, name: 'Tzadi' },
  'ק': { standard: 100, ordinal: 19, name: 'Qof' },
  'ר': { standard: 200, ordinal: 20, name: 'Resh' },
  'ש': { standard: 300, ordinal: 21, name: 'Shin' },
  'ת': { standard: 400, ordinal: 22, name: 'Tav' },
  
  // Final forms (sofit)
  'ך': { standard: 500, ordinal: 23, name: 'Kaf Sofit' },
  'ם': { standard: 600, ordinal: 24, name: 'Mem Sofit' },
  'ן': { standard: 700, ordinal: 25, name: 'Nun Sofit' },
  'ף': { standard: 800, ordinal: 26, name: 'Pe Sofit' },
  'ץ': { standard: 900, ordinal: 27, name: 'Tzadi Sofit' }
};

// Helper function to reduce to single digit
const reduceToSingleDigit = (value) => {
  while (value > 9) {
    value = value.toString().split('').reduce((sum, digit) => sum + parseInt(digit), 0);
  }
  return value;
};

// Hebrew Gematria Ciphers
export const hebrewCiphers = {
  // 1. Mispar Hechrachi (Standard Gematria)
  misparhec: {
    name: "Mispar Hechrachi",
    nameHebrew: "מספר הכרחי",
    description: "Standard Hebrew gematria: א=1, ב=2, ..., ת=400",
    category: "Simple",
    values: Object.keys(hebrewAlphabet).reduce((acc, letter) => {
      acc[letter] = hebrewAlphabet[letter].standard;
      return acc;
    }, {})
  },

  // 2. Mispar Gadol (Extended with finals)
  mispargadol: {
    name: "Mispar Gadol",
    nameHebrew: "מספר גדול",
    description: "Extended values with final letters: ך=500, ם=600, ן=700, ף=800, ץ=900",
    category: "Simple",
    values: Object.keys(hebrewAlphabet).reduce((acc, letter) => {
      acc[letter] = hebrewAlphabet[letter].standard;
      return acc;
    }, {})
  },

  // 3. Mispar Siduri (Ordinal)
  misparsiduri: {
    name: "Mispar Siduri",
    nameHebrew: "מספר סידורי",
    description: "Sequential numbering: א=1, ב=2, ..., ת=22, finals 23-27",
    category: "Simple",
    values: Object.keys(hebrewAlphabet).reduce((acc, letter) => {
      acc[letter] = hebrewAlphabet[letter].ordinal;
      return acc;
    }, {})
  },

  // 4. Mispar Katan (Reduced)
  misparkatan: {
    name: "Mispar Katan",
    nameHebrew: "מספר קטן",
    description: "Reduced values: י=1 (not 10), כ=2 (not 20), ת=4 (not 400)",
    category: "Reduction",
    values: Object.keys(hebrewAlphabet).reduce((acc, letter) => {
      const standard = hebrewAlphabet[letter].standard;
      acc[letter] = reduceToSingleDigit(standard);
      return acc;
    }, {})
  },

  // 5. Mispar Perati (Squared)
  misparperati: {
    name: "Mispar Perati",
    nameHebrew: "מספר פרטי",
    description: "Squared values: א=1², ב=4, ג=9, etc.",
    category: "Mathematical",
    values: Object.keys(hebrewAlphabet).reduce((acc, letter) => {
      const ordinal = hebrewAlphabet[letter].ordinal;
      acc[letter] = ordinal * ordinal;
      return acc;
    }, {})
  },

  // 6. AtBash (Reverse substitution)
  atbash: {
    name: "AtBash",
    nameHebrew: "אתב״ש",
    description: "First↔Last substitution: א↔ת, ב↔ש, ג↔ר, etc.",
    category: "Substitution",
    values: {
      'א': 400, 'ב': 300, 'ג': 200, 'ד': 100, 'ה': 90, 'ו': 80, 'ז': 70, 'ח': 60, 'ט': 50,
      'י': 40, 'כ': 30, 'ל': 20, 'מ': 10, 'נ': 9, 'ס': 8, 'ע': 7, 'פ': 6, 'צ': 5,
      'ק': 4, 'ר': 3, 'ש': 2, 'ת': 1,
      'ך': 10, 'ם': 20, 'ן': 9, 'ף': 6, 'ץ': 5
    }
  },

  // 7. AlBam (Half-alphabet swap)
  albam: {
    name: "AlBam",
    nameHebrew: "אלב״ם",
    description: "Alphabet split in half, swap: א↔ל, ב↔מ, ג↔נ, etc.",
    category: "Substitution",
    values: {
      'א': 30, 'ב': 40, 'ג': 50, 'ד': 60, 'ה': 70, 'ו': 80, 'ז': 90, 'ח': 100, 'ט': 200, 'י': 300, 'כ': 400,
      'ל': 1, 'מ': 2, 'נ': 3, 'ס': 4, 'ע': 5, 'פ': 6, 'צ': 7, 'ק': 8, 'ר': 9, 'ש': 10, 'ת': 20,
      'ך': 400, 'ם': 2, 'ן': 3, 'ף': 6, 'ץ': 7
    }
  },

  // 8. Avgad (Next letter)
  avgad: {
    name: "Avgad",
    nameHebrew: "אבג״ד",
    description: "Each letter becomes next: א→ב, ב→ג, ת→א",
    category: "Substitution",
    values: {
      'א': 2, 'ב': 3, 'ג': 4, 'ד': 5, 'ה': 6, 'ו': 7, 'ז': 8, 'ח': 9, 'ט': 10,
      'י': 20, 'כ': 30, 'ל': 40, 'מ': 50, 'נ': 60, 'ס': 70, 'ע': 80, 'פ': 90, 'צ': 100,
      'ק': 200, 'ר': 300, 'ש': 400, 'ת': 1,
      'ך': 30, 'ם': 50, 'ן': 60, 'ף': 90, 'ץ': 100
    }
  },

  // 9. Reverse Avgad (Previous letter)
  reverseavgad: {
    name: "Reverse Avgad",
    nameHebrew: "אבג״ד הפוך",
    description: "Each letter becomes previous: ב→א, א→ת",
    category: "Substitution",
    values: {
      'א': 400, 'ב': 1, 'ג': 2, 'ד': 3, 'ה': 4, 'ו': 5, 'ז': 6, 'ח': 7, 'ט': 8,
      'י': 9, 'כ': 10, 'ל': 20, 'מ': 30, 'נ': 40, 'ס': 50, 'ע': 60, 'פ': 70, 'צ': 80,
      'ק': 90, 'ר': 100, 'ש': 200, 'ת': 300,
      'ך': 10, 'ם': 30, 'ן': 40, 'ף': 70, 'ץ': 80
    }
  },

  // 10. Mispar Meshulash (Cubed)
  misparmeshulash: {
    name: "Mispar Meshulash",
    nameHebrew: "מספר משולש",
    description: "Cubed values: א=1³, ב=8, ג=27, etc.",
    category: "Mathematical",
    values: Object.keys(hebrewAlphabet).reduce((acc, letter) => {
      const ordinal = hebrewAlphabet[letter].ordinal;
      acc[letter] = ordinal * ordinal * ordinal;
      return acc;
    }, {})
  },

  // 11. Mispar Kidmi (Triangular)
  misparkidmi: {
    name: "Mispar Kidmi",
    nameHebrew: "מספר קדמי",
    description: "Cumulative sum: א=1, ב=3 (1+2), ג=6 (1+2+3), etc.",
    category: "Mathematical",
    values: Object.keys(hebrewAlphabet).reduce((acc, letter) => {
      const ordinal = hebrewAlphabet[letter].ordinal;
      acc[letter] = (ordinal * (ordinal + 1)) / 2;
      return acc;
    }, {})
  },

  // 12. AtBach (3-group substitution)
  atbach: {
    name: "AtBach",
    nameHebrew: "אטב״ח",
    description: "Three groups of 9 letters, swap within groups: א↔ט, י↔צ, ק↔ץ",
    category: "Substitution",
    values: {
      'א': 9, 'ב': 8, 'ג': 7, 'ד': 6, 'ה': 5, 'ו': 4, 'ז': 3, 'ח': 2, 'ט': 1,
      'י': 90, 'כ': 80, 'ל': 70, 'מ': 60, 'נ': 50, 'ס': 40, 'ע': 30, 'פ': 20, 'צ': 10,
      'ק': 900, 'ר': 800, 'ש': 700, 'ת': 600, 'ך': 500, 'ם': 400, 'ן': 300, 'ף': 200, 'ץ': 100
    }
  },

  // 13. Mispar Musafi (Additive)
  misparmusafi: {
    name: "Mispar Musafi",
    nameHebrew: "מספר מוספי",
    description: "Standard value + number of letters in word",
    category: "Additive",
    isAdditive: true
  },

  // 14. Mispar Kolel (Word Count)
  misparkolel: {
    name: "Mispar Kolel",
    nameHebrew: "מספר כלל",
    description: "Standard value + number of words in phrase",
    category: "Additive",
    isWordCount: true
  },

  // 15. Mispar Bone'eh (Cumulative Building)
  misparboneeh: {
    name: "Mispar Bone'eh",
    nameHebrew: "מספר בונה",
    description: "Each letter adds to cumulative sum within word",
    category: "Cumulative",
    isCumulative: true
  },

  // 16. Ayak Bachar (Rotation)
  ayakbachar: {
    name: "Ayak Bachar",
    nameHebrew: "אי״ק בכ״ר",
    description: "Rotates values across units, tens, hundreds groups",
    category: "Substitution",
    values: {
      'א': 10, 'ב': 20, 'ג': 30, 'ד': 40, 'ה': 50, 'ו': 60, 'ז': 70, 'ח': 80, 'ט': 90,
      'י': 100, 'כ': 200, 'ל': 300, 'מ': 400, 'נ': 500, 'ס': 600, 'ע': 700, 'פ': 800, 'צ': 900,
      'ק': 1, 'ר': 2, 'ש': 3, 'ת': 4, 'ך': 5, 'ם': 6, 'ן': 7, 'ף': 8, 'ץ': 9
    }
  },

  // 17. Ofanim (Last Letter of Name)
  ofanim: {
    name: "Ofanim",
    nameHebrew: "אופנים",
    description: "Uses last letter of letter's name: Alef→Peh, Bet→Tav",
    category: "Substitution",
    values: {
      'א': 80, 'ב': 400, 'ג': 30, 'ד': 400, 'ה': 5, 'ו': 6, 'ז': 50, 'ח': 400, 'ט': 400,
      'י': 4, 'כ': 80, 'ל': 4, 'מ': 40, 'נ': 50, 'ס': 20, 'ע': 50, 'פ': 80, 'צ': 10,
      'ק': 80, 'ר': 300, 'ש': 50, 'ת': 6, 'ך': 80, 'ם': 40, 'ן': 50, 'ף': 80, 'ץ': 10
    }
  },

  // 18. Achas Beta (7-7-8 Group Rotation)
  achasbeta: {
    name: "Achas Beta",
    nameHebrew: "אח״ס בט״א",
    description: "Splits alphabet into 7-7-8 groups with rotation",
    category: "Substitution",
    values: {
      'א': 8, 'ב': 9, 'ג': 10, 'ד': 20, 'ה': 30, 'ו': 40, 'ז': 50,
      'ח': 60, 'ט': 1, 'י': 2, 'כ': 3, 'ל': 4, 'מ': 5, 'נ': 6,
      'ס': 7, 'ע': 70, 'פ': 80, 'צ': 90, 'ק': 100, 'ר': 200, 'ש': 300, 'ת': 400,
      'ך': 3, 'ם': 5, 'ן': 6, 'ף': 80, 'ץ': 90
    }
  },

  // 19. Mispar Shemi (Spelled-out Name Value)
  misparshemi: {
    name: "Mispar Shemi",
    nameHebrew: "מספר שמי",
    description: "Value of letter's spelled-out name: Alef=אלף=111",
    category: "Complex",
    values: {
      'א': 111, 'ב': 412, 'ג': 83, 'ד': 434, 'ה': 6, 'ו': 13, 'ז': 67, 'ח': 418, 'ט': 419,
      'י': 20, 'כ': 100, 'ל': 74, 'מ': 80, 'נ': 106, 'ס': 120, 'ע': 130, 'פ': 81, 'צ': 104,
      'ק': 186, 'ר': 510, 'ש': 360, 'ת': 406, 'ך': 100, 'ם': 80, 'ן': 106, 'ף': 81, 'ץ': 104
    }
  },

  // 20. Mispar Ne'elam (Hidden Value)
  misparneelam: {
    name: "Mispar Ne'elam",
    nameHebrew: "מספר נעלם",
    description: "Spelled-out name value minus the letter itself",
    category: "Complex",
    values: {
      'א': 110, 'ב': 410, 'ג': 80, 'ד': 430, 'ה': 1, 'ו': 7, 'ז': 60, 'ח': 410, 'ט': 410,
      'י': 10, 'כ': 80, 'ל': 44, 'מ': 40, 'נ': 56, 'ס': 60, 'ע': 60, 'פ': 1, 'צ': 14,
      'ק': 86, 'ר': 310, 'ש': 60, 'ת': 6, 'ך': 80, 'ם': 40, 'ן': 56, 'ף': 1, 'ץ': 14
    }
  }
};

// Group Hebrew ciphers by category
export const hebrewCipherCategories = {
  Simple: ['misparhec', 'mispargadol', 'misparsiduri'],
  Reduction: ['misparkatan'],
  Substitution: ['atbash', 'albam', 'avgad', 'reverseavgad', 'atbach', 'ayakbachar', 'ofanim', 'achasbeta'],
  Mathematical: ['misparperati', 'misparmeshulash', 'misparkidmi'],
  Additive: ['misparmusafi', 'misparkolel'],
  Cumulative: ['misparboneeh'],
  Complex: ['misparshemi', 'misparneelam']
};

// Helper function to check if text contains Hebrew characters
export const isHebrewText = (text) => {
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text);
};

// Helper function to validate Hebrew text
export const validateHebrewText = (text) => {
  // Allow Hebrew letters, spaces, and common punctuation
  const validHebrewRegex = /^[\u0590-\u05FF\s.,!?;:'"״׳\-]+$/;
  return validHebrewRegex.test(text);
};

// Process Hebrew text with cipher
export const processHebrewText = (text, cipher) => {
  const words = text.split(/\s+/).filter(word => word.length > 0);
  let totalValue = 0;
  const wordValues = [];
  const letterBreakdowns = [];

  // Handle special cipher types
  if (cipher.isAdditive) {
    // Mispar Musafi: standard value + letter count (Hebrew letters only)
    words.forEach(word => {
      let wordValue = 0;
      let letterBreakdown = '';
      let hebrewLetterCount = 0;

      for (let i = 0; i < word.length; i++) {
        const char = word[i];
        if (hebrewCiphers.misparhec.values[char] !== undefined) {
          const value = hebrewCiphers.misparhec.values[char];
          wordValue += value;
          letterBreakdown += char + '=' + value + ' ';
          hebrewLetterCount++;
        }
      }

      // Add Hebrew letter count only
      wordValue += hebrewLetterCount;
      letterBreakdown += `(+${hebrewLetterCount} letters)`;

      totalValue += wordValue;
      wordValues.push({ word: word, value: wordValue });
      letterBreakdowns.push(letterBreakdown.trim());
    });

    return { totalValue, wordValues, letterBreakdowns };
  }

  if (cipher.isWordCount) {
    // Mispar Kolel: standard value + word count
    words.forEach(word => {
      let wordValue = 0;
      let letterBreakdown = '';

      for (let i = 0; i < word.length; i++) {
        const char = word[i];
        if (hebrewCiphers.misparhec.values[char] !== undefined) {
          const value = hebrewCiphers.misparhec.values[char];
          wordValue += value;
          letterBreakdown += char + '=' + value + ' ';
        }
      }

      totalValue += wordValue;
      wordValues.push({ word: word, value: wordValue });
      letterBreakdowns.push(letterBreakdown.trim());
    });

    // Add word count to total
    totalValue += words.length;
    letterBreakdowns[letterBreakdowns.length - 1] += ` (+${words.length} words)`;

    return { totalValue, wordValues, letterBreakdowns };
  }

  if (cipher.isCumulative) {
    // Mispar Bone'eh: cumulative building within each word
    words.forEach(word => {
      let wordValue = 0;
      let cumulativeSum = 0;
      let letterBreakdown = '';

      for (let i = 0; i < word.length; i++) {
        const char = word[i];
        if (hebrewCiphers.misparhec.values[char] !== undefined) {
          const value = hebrewCiphers.misparhec.values[char];
          cumulativeSum += value;
          wordValue += cumulativeSum;
          letterBreakdown += char + '=' + cumulativeSum + ' ';
        }
      }

      totalValue += wordValue;
      wordValues.push({ word: word, value: wordValue });
      letterBreakdowns.push(letterBreakdown.trim());
    });

    return { totalValue, wordValues, letterBreakdowns };
  }

  // Standard processing for regular ciphers
  words.forEach(word => {
    let wordValue = 0;
    let letterBreakdown = '';

    for (let i = 0; i < word.length; i++) {
      const char = word[i];
      
      if (cipher.values[char] !== undefined) {
        const value = cipher.values[char];
        wordValue += value;
        letterBreakdown += char + '=' + value + ' ';
      }
    }

    totalValue += wordValue;
    wordValues.push({
      word: word,
      value: wordValue
    });
    letterBreakdowns.push(letterBreakdown.trim());
  });

  return {
    totalValue,
    wordValues,
    letterBreakdowns
  };
};
